import { useMutation, useQueryClient } from "@tanstack/react-query";
import { dropinApi } from "@bw/shared/api";
import type { QueueEntryResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * Join a court's LINE on the self-serve board (POST
 * /facilities/{id}/drop-in/courts/{courtId}/line → QueueEntryResponse).
 *
 * Self-serve court LINES (ADR-025 Phase D, #1741): used when the caller can't
 * immediately claim a court (full, or in_game with no open slots) — a
 * court-scoped waiting entry, the self-serve analogue of useJoinQueue's
 * facility-level entry. The backend enforces "one spot anywhere": a caller who
 * already holds an active entry — a facility queue entry, a line for this or
 * another court, or a court assignment — gets a structured 409
 * (AlreadyInQueueError, code ALREADY_IN_QUEUE). The mutation does NOT swallow
 * errors; the page maps them to honest copy (mapJoinLineError).
 *
 * Leaving a line reuses the existing self-leave contract (queueApi.leave on the
 * entry_id) through useLeaveLine; a line entry remains a regular queue entry.
 *
 * On settlement the board and caller-entry caches are invalidated so success
 * and stale-state rejection both re-read server truth.
 */
export function useJoinLine(facilityId: string) {
  const queryClient = useQueryClient();
  return useMutation<QueueEntryResponse, unknown, string>({
    mutationFn: (courtId: string) => dropinApi.joinLine(facilityId, courtId),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropin.courtBoard(facilityId),
        refetchType: "active",
      });
      void queryClient.invalidateQueries({
        queryKey: ["dropin", "my-entry", facilityId],
        refetchType: "active",
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropin.myParty(facilityId),
        refetchType: "active",
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropin.partyMemberships(facilityId),
        refetchType: "active",
      });
    },
  });
}
